import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Container } from '@mui/material'
import {
  useReasoningTrace,
  downloadTraceFile,
} from '@/services/reasoning-traces/reasoning-traces.service'
import { PhaseStatus, OutputType } from '@/services/reasoning-traces/types'
import Header from '../../header'
import Button from '@/components/ui/button'
import {
  DetailWrapper,
  BackButton,
  StatusBadge,
  MetadataSection,
  MetadataItem,
  PhasesSection,
  PhaseCard,
  PhaseHeader,
  PhaseContent,
  OutputCard,
  CodeBlock,
  FileCard,
  FileGrid,
  ReasoningStep,
  LoadingWrapper,
  ErrorState,
  SimpleCard,
} from './styled'

const ReasoningTraceDetail: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>()
  const navigate = useNavigate()
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set())

  const { data: trace, isLoading, error } = useReasoningTrace(sessionId!)

  const handleBack = () => {
    navigate('/reasoning-traces')
  }

  const togglePhase = (phaseId: string) => {
    const newExpanded = new Set(expandedPhases)
    if (newExpanded.has(phaseId)) {
      newExpanded.delete(phaseId)
    } else {
      newExpanded.add(phaseId)
    }
    setExpandedPhases(newExpanded)
  }

  const handleFileDownload = async (fileId: string, filename: string) => {
    if (trace) {
      await downloadTraceFile(trace.session_id, fileId, filename)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case PhaseStatus.COMPLETED:
      case 'completed':
        return '#4BA765'
      case PhaseStatus.RUNNING:
      case 'running':
        return '#FFA500'
      case PhaseStatus.ERROR:
      case 'error':
        return '#FF4444'
      default:
        return '#666666'
    }
  }

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-'
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    if (hours > 0) {
      return `${hours}h ${minutes}m ${secs}s`
    } else if (minutes > 0) {
      return `${minutes}m ${secs}s`
    }
    return `${secs}s`
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    if (bytes < 1024 * 1024 * 1024)
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    return (bytes / (1024 * 1024 * 1024)).toFixed(1) + ' GB'
  }

  const renderSimpleMarkdown = (text: string) => {
    if (!text) return null

    // Simple markdown parsing for basic formatting
    let formatted = text

    // Headers (h1-h6)
    formatted = formatted.replace(/^###### (.*?)$/gm, '<h6>$1</h6>')
    formatted = formatted.replace(/^##### (.*?)$/gm, '<h5>$1</h5>')
    formatted = formatted.replace(/^#### (.*?)$/gm, '<h4>$1</h4>')
    formatted = formatted.replace(/^### (.*?)$/gm, '<h3>$1</h3>')
    formatted = formatted.replace(/^## (.*?)$/gm, '<h2>$1</h2>')
    formatted = formatted.replace(/^# (.*?)$/gm, '<h1>$1</h1>')

    // Bold
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    formatted = formatted.replace(/__(.*?)__/g, '<strong>$1</strong>')

    // Italic
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>')
    formatted = formatted.replace(/_(.*?)_/g, '<em>$1</em>')

    // Code blocks
    formatted = formatted.replace(
      /```([\s\S]*?)```/g,
      '<pre style="background:#f6f8fa;padding:12px;border-radius:6px;overflow-x:auto;"><code>$1</code></pre>',
    )

    // Inline code
    formatted = formatted.replace(
      /`(.*?)`/g,
      '<code style="background:#f6f8fa;padding:2px 4px;border-radius:3px;">$1</code>',
    )

    // Line breaks
    formatted = formatted.replace(/\n/g, '<br />')

    // Lists (basic support)
    formatted = formatted.replace(/^[\*\-\+] (.+)$/gm, '<li>$1</li>')
    formatted = formatted.replace(
      /(<li>.*<\/li>)/s,
      '<ul style="margin:8px 0;padding-left:20px;">$1</ul>',
    )

    return <div dangerouslySetInnerHTML={{ __html: formatted }} />
  }

  const renderDataSummaryTable = (data: any) => {
    if (!data) return null

    // Handle data_preview format which can have either:
    // 1. { data_preview: { title, subtitle, headers: [], rows: [[]] } }
    // 2. { data_preview: { title, subtitle, columns: [], rows: [{col: val}] } }
    if (data.data_preview) {
      const preview = data.data_preview
      const title = preview.title || data.title
      const subtitle = preview.subtitle || data.subtitle

      // Check if we have headers/rows format (array of arrays)
      if (preview.headers && Array.isArray(preview.rows)) {
        const headers = preview.headers
        const rows = preview.rows

        return (
          <div>
            {title && (
              <h4 style={{ marginBottom: '8px', color: '#212529' }}>{title}</h4>
            )}
            {subtitle && (
              <p
                style={{
                  marginBottom: '16px',
                  color: '#6c757d',
                  fontSize: '14px',
                }}
              >
                {subtitle}
              </p>
            )}
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '14px',
                }}
              >
                {headers.length > 0 && (
                  <thead>
                    <tr>
                      {headers.map((header: string, idx: number) => (
                        <th
                          key={idx}
                          style={{
                            padding: '12px 8px',
                            borderBottom: '2px solid #dee2e6',
                            textAlign: 'left',
                            fontWeight: 600,
                            color: '#495057',
                            backgroundColor: '#f8f9fa',
                          }}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}
                <tbody>
                  {rows.map((row: any[], rowIdx: number) => (
                    <tr key={rowIdx}>
                      {Array.isArray(row)
                        ? row.map((cell: any, cellIdx: number) => (
                            <td
                              key={cellIdx}
                              style={{
                                padding: '10px 8px',
                                borderBottom: '1px solid #dee2e6',
                                color: '#212529',
                              }}
                            >
                              {typeof cell === 'object'
                                ? JSON.stringify(cell)
                                : String(cell || '')}
                            </td>
                          ))
                        : headers.map((header: string, cellIdx: number) => (
                            <td
                              key={cellIdx}
                              style={{
                                padding: '10px 8px',
                                borderBottom: '1px solid #dee2e6',
                                color: '#212529',
                              }}
                            >
                              {typeof row[header] === 'object'
                                ? JSON.stringify(row[header])
                                : String(row[header] || '')}
                            </td>
                          ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      }

      // Handle columns/rows format (array of objects)
      const headers = preview.columns || []
      const rows = preview.rows || []

      return (
        <div>
          {title && (
            <h4 style={{ marginBottom: '8px', color: '#212529' }}>{title}</h4>
          )}
          {subtitle && (
            <p
              style={{
                marginBottom: '16px',
                color: '#6c757d',
                fontSize: '14px',
              }}
            >
              {subtitle}
            </p>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px',
              }}
            >
              {headers.length > 0 && (
                <thead>
                  <tr>
                    {headers.map((header: string, idx: number) => (
                      <th
                        key={idx}
                        style={{
                          padding: '12px 8px',
                          borderBottom: '2px solid #dee2e6',
                          textAlign: 'left',
                          fontWeight: 600,
                          color: '#495057',
                          backgroundColor: '#f8f9fa',
                        }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {rows.map((row: any, rowIdx: number) => (
                  <tr key={rowIdx}>
                    {headers.map((header: string, cellIdx: number) => (
                      <td
                        key={cellIdx}
                        style={{
                          padding: '10px 8px',
                          borderBottom: '1px solid #dee2e6',
                          color: '#212529',
                        }}
                      >
                        {typeof row[header] === 'object'
                          ? JSON.stringify(row[header])
                          : String(row[header] || '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )
    }

    // Simple table format - expects { title?, subtitle?, headers: [], rows: [[]] }
    if (data.headers && data.rows) {
      return (
        <div>
          {data.title && (
            <h4 style={{ marginBottom: '8px', color: '#212529' }}>
              {data.title}
            </h4>
          )}
          {data.subtitle && (
            <p
              style={{
                marginBottom: '16px',
                color: '#6c757d',
                fontSize: '14px',
              }}
            >
              {data.subtitle}
            </p>
          )}
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontSize: '14px',
              }}
            >
              {data.headers && data.headers.length > 0 && (
                <thead>
                  <tr>
                    {data.headers.map((header: string, idx: number) => (
                      <th
                        key={idx}
                        style={{
                          padding: '12px 8px',
                          borderBottom: '2px solid #dee2e6',
                          textAlign: 'left',
                          fontWeight: 600,
                          color: '#495057',
                          backgroundColor: '#f8f9fa',
                        }}
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {data.rows.map((row: any[], rowIdx: number) => (
                  <tr key={rowIdx}>
                    {row.map((cell: any, cellIdx: number) => (
                      <td
                        key={cellIdx}
                        style={{
                          padding: '10px 8px',
                          borderBottom: '1px solid #dee2e6',
                          color: '#212529',
                        }}
                      >
                        {typeof cell === 'object'
                          ? JSON.stringify(cell)
                          : String(cell || '')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )
    }

    // Fallback to simple key-value display
    return (
      <div style={{ marginTop: '16px' }}>
        <pre
          style={{
            backgroundColor: '#f8f9fa',
            padding: '12px',
            borderRadius: '4px',
            fontSize: '13px',
            overflow: 'auto',
          }}
        >
          {JSON.stringify(data, null, 2)}
        </pre>
      </div>
    )
  }

  const renderOutput = (output: any) => {
    // Handle both output_type, outputType, and type for compatibility
    const outputType =
      output.output_type ||
      output.outputType ||
      output.type ||
      output.data?.type

    switch (outputType) {
      case OutputType.TEXT_MESSAGE:
        return (
          <OutputCard key={output.id}>
            <p>{output.data.message}</p>
          </OutputCard>
        )

      case OutputType.CODE_BLOCK:
      case 'CODE_BLOCK':
        // Handle both formats: direct and nested content
        // The backend sends: { type: 'CODE_BLOCK', content: { language, code }, description }
        const codeContent =
          output.content || output.data?.content || output.data
        const language = codeContent?.language || 'text'
        const code = codeContent?.code || ''

        return (
          <OutputCard key={output.id}>
            {(output.description || output.data?.description) && (
              <h4>{output.description || output.data?.description}</h4>
            )}
            <CodeBlock>
              <pre>
                <code className={`language-${language}`}>{code}</code>
              </pre>
            </CodeBlock>
          </OutputCard>
        )

      case OutputType.MARKDOWN_TEXT:
      case 'MARKDOWN_TEXT':
        return (
          <OutputCard key={output.id}>
            {output.description && <h4>{output.description}</h4>}
            <div className="markdown-content">
              {renderSimpleMarkdown(output.data?.content || output.data || '')}
            </div>
          </OutputCard>
        )

      case OutputType.PLOT_IMAGE:
        return (
          <OutputCard key={output.id}>
            {output.data.title && <h4>{output.data.title}</h4>}
            {output.data.description && <p>{output.data.description}</p>}
            <img
              src={output.data.path}
              alt={output.data.title || 'Plot'}
              style={{ maxWidth: '100%', marginTop: '12px' }}
            />
          </OutputCard>
        )

      case OutputType.FILE_REFERENCE:
        return (
          <OutputCard key={output.id}>
            {output.description && <h4>{output.description}</h4>}
            <FileCard>
              <span className="file-type">
                {output.data.fileType?.toUpperCase() || 'FILE'}
              </span>
              <span className="file-path">{output.data.path}</span>
            </FileCard>
          </OutputCard>
        )

      case OutputType.ERROR_MESSAGE:
        return (
          <OutputCard key={output.id} className="error">
            <h4>Error</h4>
            <p>{output.data.error}</p>
            {output.data.traceback && (
              <CodeBlock>
                <pre>{output.data.traceback}</pre>
              </CodeBlock>
            )}
          </OutputCard>
        )

      case OutputType.DATA_SUMMARY_TABLE:
      case 'DATA_SUMMARY_TABLE': // Handle string literal as well
        // Check if data is directly in output.data (new format from trace)
        const hasDirectData = output.data?.headers && output.data?.rows

        // Prepare table data based on the format
        const tableData = hasDirectData
          ? output.data // Use data directly when it has headers/rows
          : {
              data_preview: output.data_preview || output.data?.data_preview,
              title: output.title,
              subtitle: output.subtitle,
            }

        return (
          <OutputCard key={output.id}>
            {(output.description || output.data?.description) && (
              <p style={{ marginBottom: '16px' }}>
                {output.description || output.data?.description}
              </p>
            )}
            {renderDataSummaryTable(tableData)}
          </OutputCard>
        )

      default:
        // For any unknown output type, try to render as simple text/markdown
        if (output.data) {
          const content =
            output.data?.content ||
            output.data?.message ||
            output.data?.text ||
            (typeof output.data === 'string' ? output.data : null)

          if (content) {
            return (
              <OutputCard key={output.id}>
                {output.description && <h4>{output.description}</h4>}
                <div>{renderSimpleMarkdown(content)}</div>
              </OutputCard>
            )
          }

          // If no text content found, show as JSON
          return (
            <OutputCard key={output.id}>
              {output.description && <h4>{output.description}</h4>}
              <pre
                style={{
                  backgroundColor: '#f6f8fa',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  overflow: 'auto',
                }}
              >
                {JSON.stringify(output.data, null, 2)}
              </pre>
            </OutputCard>
          )
        }
        return null
    }
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ padding: '30px' }}>
        <ErrorState>
          <h3>Error loading reasoning trace</h3>
          <p>{error instanceof Error ? error.message : 'An error occurred'}</p>
          <Button onClick={handleBack}>Back to List</Button>
        </ErrorState>
      </Container>
    )
  }

  if (isLoading || !trace) {
    return (
      <Container maxWidth="xl" sx={{ padding: '30px' }}>
        <LoadingWrapper>
          <p>Loading reasoning trace...</p>
        </LoadingWrapper>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ padding: '30px' }}>
      <BackButton onClick={handleBack}>← Back to Reasoning Traces</BackButton>

      <Header
        title={trace.title}
        description={`Session ID: ${trace.session_id}`}
      >
        <StatusBadge color={getStatusColor(trace.status)}>
          {trace.status}
        </StatusBadge>
      </Header>

      <DetailWrapper>
        <MetadataSection>
          <SimpleCard>
            <h3>Session Information</h3>
            <div className="metadata-grid">
              <MetadataItem>
                <span className="label">Started At</span>
                <span className="value">
                  {new Date(trace.started_at).toLocaleString()}
                </span>
              </MetadataItem>
              {trace.ended_at && (
                <MetadataItem>
                  <span className="label">Ended At</span>
                  <span className="value">
                    {new Date(trace.ended_at).toLocaleString()}
                  </span>
                </MetadataItem>
              )}
              <MetadataItem>
                <span className="label">Duration</span>
                <span className="value">{formatDuration(trace.duration)}</span>
              </MetadataItem>
              {Object.entries(trace.metadata).map(([key, value]) => (
                <MetadataItem key={key}>
                  <span className="label">
                    {key.replace(/_/g, ' ').toUpperCase()}
                  </span>
                  <span className="value">{String(value)}</span>
                </MetadataItem>
              ))}
            </div>
          </SimpleCard>
        </MetadataSection>

        {/* Datasources section hidden for now */}
        {false && trace.datasources && trace.datasources.length > 0 && (
          <SimpleCard style={{ marginBottom: '24px', padding: '16px' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px',
              }}
            >
              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>
                Data Sources ({trace.datasources.length})
              </h4>
              <Button
                variant="secondary"
                onClick={() => navigate('/datasources')}
                style={{ fontSize: '12px', padding: '6px 12px' }}
              >
                View All Sources
              </Button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {trace.datasources.map((ds) => (
                <div
                  key={ds.datasource_id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '6px 12px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '16px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s',
                  }}
                  onClick={() => navigate('/datasources')}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = '#e8e8e8')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = '#f5f5f5')
                  }
                >
                  <span style={{ fontWeight: 500 }}>{ds.name}</span>
                  <span style={{ color: '#666', marginLeft: '6px' }}>
                    ({ds.usage_count || 0} queries)
                  </span>
                </div>
              ))}
            </div>
          </SimpleCard>
        )}

        {/* Workspace Files Section */}
        {trace.workspace_files && trace.workspace_files.length > 0 && (
          <SimpleCard style={{ marginBottom: '32px' }}>
            <h3>Reports & Analysis ({trace.workspace_files.length} files)</h3>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                marginTop: '16px',
              }}
            >
              {trace.workspace_files.map((file, index) => {
                const fileExtension = file.name.split('.').pop()?.toLowerCase()
                const isImage = ['png', 'jpg', 'jpeg', 'gif', 'svg'].includes(
                  fileExtension || '',
                )
                const isHtml = fileExtension === 'html'
                const isJson = fileExtension === 'json'

                return (
                  <div
                    key={`${file.name}-${index}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '12px 16px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '8px',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        flex: 1,
                      }}
                    >
                      <span className="file-icon" style={{ fontSize: '20px' }}>
                        {isImage ? '🖼️' : isHtml ? '🌐' : isJson ? '📊' : '📄'}
                      </span>
                      <span
                        className="file-name"
                        style={{
                          fontSize: '14px',
                          fontWeight: 500,
                          flex: 1,
                        }}
                      >
                        {file.name}
                      </span>
                      <span
                        className="file-type"
                        style={{
                          fontSize: '11px',
                          color: '#6c757d',
                          textTransform: 'uppercase',
                          backgroundColor: '#fff',
                          padding: '2px 8px',
                          borderRadius: '12px',
                          fontWeight: 600,
                        }}
                      >
                        {fileExtension}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {(isHtml || isImage) && (
                        <Button
                          variant="primary"
                          onClick={() => {
                            // Simple direct opening in new tab
                            window.open(file.url, '_blank')
                          }}
                        >
                          {isHtml ? 'View Report' : 'View'}
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        onClick={() => {
                          const link = document.createElement('a')
                          link.href = file.url
                          link.download = file.name
                          link.target = '_blank'
                          document.body.appendChild(link)
                          link.click()
                          document.body.removeChild(link)
                        }}
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          </SimpleCard>
        )}

        <PhasesSection>
          <h2>Reasoning Phases ({trace.phases.length})</h2>
          {trace.phases.map((phase, index) => (
            <PhaseCard key={phase.phase_id}>
              <PhaseHeader
                onClick={() => togglePhase(phase.phase_id)}
                className={expandedPhases.has(phase.phase_id) ? 'expanded' : ''}
              >
                <div className="phase-title">
                  <span className="phase-number">Phase {index + 1}</span>
                  <h3>{phase.title}</h3>
                  <StatusBadge color={getStatusColor(phase.status)}>
                    {phase.status}
                  </StatusBadge>
                </div>
                <span className="expand-icon">
                  {expandedPhases.has(phase.phase_id) ? '−' : '+'}
                </span>
              </PhaseHeader>

              {expandedPhases.has(phase.phase_id) && (
                <PhaseContent>
                  {phase.goal && (
                    <div className="phase-goal">
                      <strong>Goal:</strong> {phase.goal}
                    </div>
                  )}

                  {phase.reasoning_steps.length > 0 && (
                    <div className="reasoning-steps">
                      <h4>Reasoning Steps</h4>
                      {phase.reasoning_steps.map((step, idx) => (
                        <ReasoningStep key={idx}>{step}</ReasoningStep>
                      ))}
                    </div>
                  )}

                  {phase.outputs.length > 0 && (
                    <div className="phase-outputs">
                      <h4>Outputs</h4>
                      {phase.outputs.map((output, outputIdx) => {
                        // Add a temporary id if it doesn't exist
                        const outputWithId = {
                          ...output,
                          id: output.id || `output_${outputIdx}`,
                        }
                        return renderOutput(outputWithId)
                      })}
                    </div>
                  )}

                  {phase.conclusion && (
                    <div className="phase-conclusion">
                      <strong>Conclusion:</strong> {phase.conclusion}
                    </div>
                  )}
                </PhaseContent>
              )}
            </PhaseCard>
          ))}
        </PhasesSection>
      </DetailWrapper>
    </Container>
  )
}

export default ReasoningTraceDetail
